import { alignGetPlainTextWords } from "../timestamp_sync/timestamp_sync.js";
// Cache for word segments by external_id
var wordSegmentsCache = null;

export const SyncCursorVideo = {
  mounted() {
    this.el.addEventListener("click", this.jumpVideoToCursor.bind(this));

    // don't request multiple times (initial request is slow)
    this.fetchTimestampInProgress = false;

    // "preparing, video will start to follow where you are in the transcript shortly." show with delay
    this.progressMessageTimeout = null;

    // Automatically trigger jumpVideoToCursor once,
    // for gems-tools to warm up caches and less wait for the first click to work
    setTimeout(() => {
      const syntheticEvent = { target: this.el };
      this.jumpVideoToCursor(syntheticEvent);
    }, 500);
  },

  destroyed() {
    wordSegmentsCache = null;
  },

  jumpVideoToCursor(event) {
    this.video = document.querySelector("#video");
    if (!this.video) return;

    if (
      document.querySelector("#manage-video-player") &&
      document
        .querySelector("#manage-video-player")
        .classList.contains("hidden")
    ) {
      return;
    }
    const { cursorPosition, value } = this.getCursorPositionAndValue(event);
    if (this.el.dataset.language == this.el.dataset.original_language) {
      const transcript = this.createTranscript(cursorPosition, value, true);
      this.get_timestamp_same_language(transcript, this.video.duration);
    } else {
      const transcript = this.createTranscript(cursorPosition, value, false);
      this.fetchTimestamp(transcript);
    }
  },

  getCursorPositionAndValue(event) {
    if (event.target.tagName.toLowerCase() === "textarea") {
      return {
        cursorPosition: event.target.selectionStart,
        value: event.target.value,
      };
    }

    return this.getSelectionInfo(event);
  },

  getSelectionInfo(event) {
    if (!window.getSelection) return { cursorPosition: 0, value: "" };

    const selection = window.getSelection();
    if (selection.rangeCount === 0) return { cursorPosition: 0, value: "" };

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    let textNode = event.target;

    if (["del", "ins"].includes(textNode.tagName.toLowerCase())) {
      textNode = textNode.parentNode;
    }

    preCaretRange.selectNodeContents(textNode);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    let cursorPosition = preCaretRange.toString().length;

    const { previousText, nextText, isDiffMode } =
      this.getPreviousText(textNode);
    cursorPosition += previousText.length;
    const value = isDiffMode
      ? previousText + textNode.innerText + nextText
      : textNode.innerText;

    return { cursorPosition, value };
  },

  getPreviousText(textNode) {
    let previousText = "";
    let nextText = "";
    let isDiffMode = false;

    if (textNode.classList.contains("d2h-code-line-ctn")) {
      isDiffMode = true;
      const tr = textNode.closest("tr");
      const diffMode = textNode.closest(".d2h-code-side-line")
        ? "side-by-side"
        : "line-by-line";
      const delOrIns = textNode.closest(".d2h-del")
        ? "del"
        : textNode.closest(".d2h-ins")
        ? "ins"
        : "";

      ({ previousText, nextText } = this.getSurroundingSiblingsText(
        tr,
        diffMode,
        delOrIns
      ));
    }

    return { previousText, nextText, isDiffMode };
  },

  getSurroundingSiblingsText(tr, diffMode, delOrIns) {
    const previousSiblingsCode = [];
    const nextSiblingsCode = [];
    const parent = tr.parentElement;
    const children = parent.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (
        i == tr.rowIndex ||
        this.shouldSkipSibling(diffMode, delOrIns, child)
      ) {
        continue;
      }
      const plaintextElement = child.querySelector(".plaintext");
      if (plaintextElement && i < tr.rowIndex) {
        previousSiblingsCode.push(plaintextElement.innerText);
      } else if (plaintextElement && i > tr.rowIndex) {
        nextSiblingsCode.push(plaintextElement.innerText);
      }
    }
    return {
      previousText: previousSiblingsCode.join("\n"),
      nextText: nextSiblingsCode.join("\n"),
    };
  },

  shouldSkipSibling(diffMode, delOrIns, tr) {
    return (
      diffMode === "line-by-line" &&
      ((delOrIns === "del" && tr.querySelector(".d2h-ins")) ||
        (delOrIns === "ins" && tr.querySelector(".d2h-del")))
    );
  },

  createTranscript(cursorPosition, value, snippetOnly = false) {
    let wordStart = cursorPosition;
    while (wordStart > 0 && !/\s/.test(value[wordStart - 1])) {
      wordStart--;
    }

    let start, end;

    if (snippetOnly) {
      //for original language, it's possible to send only a small part of the text for better performance. translation editing needs whole text.
      start = Math.max(0, wordStart - 150);
      end = Math.min(value.length, wordStart + 150);
    } else {
      start = 0;
      end = value.length;
    }

    const textBeforeWord = value.substring(start, wordStart);
    const textAfterWord = value.substring(wordStart, end);

    return {
      transcript_snippet: `${textBeforeWord} [CURSOR_POSITION] ${textAfterWord}`,
      start_word_index: 0,
      end_word_index: 0,
    };
  },

  async get_timestamp_same_language(transcript, video_duration) {
    const current_time = new Date().getTime();
    const external_id = this.el.dataset.external_id;

    // Check if we have cached word segments for this external_id
    if (!wordSegmentsCache) {
      const token = this.el.dataset.token;
      if (!token) return;

      const response = await fetch(
        `${this.el.dataset.libUrl}${external_id}/initial_whisper_word_segments_with_timestamps.json${token}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      wordSegmentsCache = data.word_segments;
    }

    const wordSegments = wordSegmentsCache;
    const plainText = transcript.transcript_snippet;

    if (!plainText.includes(" [CURSOR_POSITION] ")) {
      console.error(
        'No " [CURSOR_POSITION] " found in plain text. A space on either side is required'
      );
      return;
    }

    const aligned_result = alignGetPlainTextWords(
      wordSegments,
      plainText,
      true,
      video_duration
    );

    for (const word of aligned_result.word_segments) {
      if (word.word.includes("[CURSOR_POSITION]")) {
        if (aligned_result.word_segments.indexOf(word) > 0) {
          //don't seek video to start
          this.video.currentTime = word.start;
          return word.start;
        }
      }
    }

    console.log("possible bug: [CURSOR_POSITION] not found in words");
  },

  fetchTimestamp(transcript) {
    // Prevent multiple concurrent requests
    if (this.fetchTimestampInProgress) {
      return;
    }

    this.fetchTimestampInProgress = true;

    this.progressMessageTimeout = setTimeout(() => {
      //delay, only show this when it's taking a long time
      document
        .getElementById("timestamps-in-progress-message-wrapper")
        .classList.remove("hidden");
    }, 2000);

    fetch(
      `${this.el.dataset.gemsToolsUrl}/get_timestamp_from_cursor_position`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          external_id: this.el.dataset.external_id,
          plain_text: transcript.transcript_snippet,
          start_word_index: transcript.start_word_index,
          end_word_index: transcript.end_word_index,
          language: this.el.dataset.language,
          original_language: this.el.dataset.original_language,
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.video.currentTime = parseFloat(data.timestamp);
        }
      })
      .catch((error) => console.error("Error fetching timestamp:", error))
      .finally(() => {
        clearTimeout(this.progressMessageTimeout);
        this.progressMessageTimeout = null;

        document
          .getElementById("timestamps-in-progress-message-wrapper")
          .classList.add("hidden");

        this.fetchTimestampInProgress = false;
      });
  },
};
